// Single source of truth for the site's languages plus the cookie/browser
// detection that drives automatic language selection.
//
// Resolution priority (highest first):
//   1. URL prefix   — /fr/... always renders French, whatever the cookie says
//                     (so shared links are honoured and SEO stays correct).
//   2. `lg` cookie  — the visitor's remembered choice. Set on every language
//                     switch and synced from the URL, so the chosen language
//                     survives navigation even through links that drop the
//                     prefix (e.g. an internal href="/wykey").
//   3. Browser lang — navigator.languages on a first visit with no cookie.
//   4. English      — fallback when nothing above matches.
//
// English lives at the site root with no prefix; the others under /<code>/.

export const SUPPORTED = [
    "en", "fr", "es", "pt", "de", "nl", "it", "pl", "ro", "el", "uk", "ja", "ko",
];

export const LANG_COOKIE = "lg";

// One year — long enough that the choice is effectively permanent.
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export const isSupported = (code?: string | null): code is string =>
    !!code && SUPPORTED.includes(code);

// Reads the `lg` cookie. Returns null when absent or holding a stale/invalid
// value, so callers can fall through to browser detection.
export const getLangCookie = (): string | null => {
    try {
        const match = document.cookie.match(/(?:^|;\s*)lg=([^;]*)/);
        const value = match ? decodeURIComponent(match[1]).toLowerCase() : null;
        return isSupported(value) ? value : null;
    } catch {
        return null;
    }
};

export const setLangCookie = (code: string): void => {
    if (!isSupported(code)) return;
    try {
        document.cookie =
            `${LANG_COOKIE}=${code};path=/;max-age=${COOKIE_MAX_AGE};samesite=lax`;
    } catch {
        /* document.cookie unavailable (SSR/prerender) — ignore */
    }
};

// Picks the first supported language from the browser's preference list,
// matching on the primary subtag (e.g. "fr-CA" -> "fr").
export const detectBrowserLang = (): string | null => {
    try {
        const list =
            navigator.languages && navigator.languages.length
                ? navigator.languages
                : [navigator.language];
        for (const tag of list) {
            const code = tag?.toLowerCase().split("-")[0];
            if (isSupported(code)) return code;
        }
    } catch {
        /* navigator unavailable — ignore */
    }
    return null;
};

// The language explicitly carried by the URL, or null for the English root.
export const langFromUrl = (pathname: string): string | null => {
    const seg = pathname.split("/")[1]?.toLowerCase();
    return seg && seg !== "en" && isSupported(seg) ? seg : null;
};

// The language the visitor should see for a given path, applying the full
// priority chain above.
export const resolveLang = (pathname: string): string =>
    langFromUrl(pathname) || getLangCookie() || detectBrowserLang() || "en";
